import { useMutation, useQueryClient } from "@tanstack/react-query";
import { EditIcon } from "lucide-react";
import { useRef, useState } from "react";
import { Dialog } from "react-aria-components";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal, ModalHeading } from "@/components/base/modal";
import { Popover } from "@/components/base/popover";
import {
  useLastSeed,
  usePoolsQueryOptions,
  useTeam,
  useTeamsQueryOptions,
} from "@/components/tournaments/context";
import { useTRPC } from "@/integrations/trpc/react";
import { editSeedSchema } from "@/lib/team-schemas";

import { TeamNames } from "../names";

export type EditSeedFormProps = {
  tournamentDivisionTeamId: number;
  seed: number;
  target: "division" | "pool";
};

export function EditSeedForm({
  tournamentDivisionTeamId,
  seed,
  target,
}: EditSeedFormProps) {
  const [isOpen, setOpen] = useState(false);
  const triggerRef = useRef(null);

  const teamsQueryOptions = useTeamsQueryOptions();
  const poolsQueryOptions = usePoolsQueryOptions();

  const lastSeed = useLastSeed();

  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, failureReason } = useMutation({
    ...trpc.tournaments.editSeed.mutationOptions(),
    onSuccess: () => {
      setOpen(false);

      if (target === "division") {
        queryClient.invalidateQueries(teamsQueryOptions);
      } else {
        queryClient.invalidateQueries(poolsQueryOptions);
      }
    },
  });

  const schema = editSeedSchema.omit({ id: true, target: true });

  const form = useAppForm({
    defaultValues: {
      seed,
    },
    validators: {
      onMount: schema,
      onChange: schema,
    },
    onSubmit: ({ value: { seed } }) => {
      mutate({
        id: tournamentDivisionTeamId,
        seed,
        target,
      });
    },
  });

  return (
    <>
      <Button
        variant="icon"
        size="sm"
        ref={triggerRef}
        onPress={() => setOpen(true)}
      >
        <EditIcon />
      </Button>
      <Popover triggerRef={triggerRef} isOpen={isOpen} onOpenChange={setOpen}>
        <Dialog aria-label="Set team and waitlist capacity">
          <form
            className="flex flex-col p-3"
            onSubmit={(e) => {
              e.preventDefault();

              form.handleSubmit();
            }}
          >
            {failureReason && (
              <form.AppForm>
                <form.Alert
                  title={"Unable to update seed"}
                  description={failureReason.message}
                />
              </form.AppForm>
            )}

            <form.AppField name="seed">
              {(field) => (
                <field.Number
                  field={field}
                  name="seed"
                  label="Desired Seed"
                  minValue={1}
                  maxValue={lastSeed}
                />
              )}
            </form.AppField>

            <form.AppForm>
              <form.Footer>
                <form.SubmitButton requireChange={false}>
                  Save
                </form.SubmitButton>
              </form.Footer>
            </form.AppForm>
          </form>
        </Dialog>
      </Popover>
    </>
  );
}

export type EditSeedFormModalProps = {
  tournamentDivisionTeamId: number;
  seed: number;
  target: "division" | "pool";
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function EditSeedFormModal({
  tournamentDivisionTeamId,
  target,
  seed,
  ...props
}: EditSeedFormModalProps) {
  const team = useTeam(tournamentDivisionTeamId);

  const teamsQueryOptions = useTeamsQueryOptions();
  const poolsQueryOptions = usePoolsQueryOptions();

  const lastSeed = useLastSeed();

  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, failureReason } = useMutation({
    ...trpc.tournaments.editSeed.mutationOptions(),
    onSuccess: () => {
      props.onOpenChange(false);

      if (target === "division") {
        queryClient.invalidateQueries(teamsQueryOptions);
      } else {
        queryClient.invalidateQueries(poolsQueryOptions);
      }
    },
  });

  const schema = editSeedSchema.omit({ id: true, target: true });

  const form = useAppForm({
    defaultValues: {
      seed,
    },
    validators: {
      onMount: schema,
      onChange: schema,
    },
    onSubmit: ({ value: { seed } }) => {
      mutate({
        id: tournamentDivisionTeamId,
        seed,
        target,
      });
    },
  });

  return (
    <Modal {...props}>
      <div className="relative flex flex-col space-y-8 p-3">
        <ModalHeading>Edit Seed</ModalHeading>

        <p>
          Edit {target} seed for{" "}
          <TeamNames
            className="font-semibold italic"
            players={team?.team.players || []}
            link={false}
          />
          .
        </p>

        <form
          className="flex flex-col p-3"
          onSubmit={(e) => {
            e.preventDefault();

            form.handleSubmit();
          }}
        >
          {failureReason && (
            <form.AppForm>
              <form.Alert
                title={"Unable to update seed"}
                description={failureReason.message}
              />
            </form.AppForm>
          )}

          <form.AppField name="seed">
            {(field) => (
              <field.Number
                field={field}
                name="seed"
                label="Desired Seed"
                minValue={1}
                maxValue={lastSeed}
              />
            )}
          </form.AppField>

          <form.AppForm>
            <form.Footer>
              <Button onPress={() => props.onOpenChange(false)}>Cancel</Button>
              <form.SubmitButton requireChange={false}>Save</form.SubmitButton>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}
